import React, { useEffect, useState } from "react";
import { CalendarClock, Plus, Trash2, RotateCw, ListMusic } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * RecurringTemplatesPanel — weekly setlist templates the host can
 * "materialize" into a one-shot scheduled set for the next occurrence.
 *
 * Use case: A host runs "Friday 8pm Hip-Hop Hour" every week. They
 * build the template once, then click Materialize each week to arm
 * the studio for that show without re-typing the queue.
 */
export default function RecurringTemplatesPanel({ slug, currentSetlist = [] }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [weekday, setWeekday] = useState(5); // Sat default
  const [hour, setHour] = useState(20);
  const [minute, setMinute] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dj/setlist/recurring");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!title.trim()) { toast.info("Add a title."); return; }
    if (!currentSetlist.length) { toast.info("Build a setlist first — the template snapshots your current queue."); return; }
    setBusy(true);
    try {
      await api.post("/dj/setlist/recurring", {
        title: title.trim(),
        station_slug: slug,
        weekday, hour, minute,
        tracks: currentSetlist.map((t) => ({
          id: t.id, title: t.title || t.name, artist: t.artist, album: t.album,
          duration_sec: t.duration_sec, source_url: t.source_url, source_type: t.source_type,
        })),
      });
      setTitle(""); setShowForm(false);
      await load();
      toast.success("Recurring template saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/dj/setlist/recurring/${id}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const materialize = async (id) => {
    try {
      const { data } = await api.post(`/dj/setlist/recurring/${id}/materialize`);
      toast.success(`Armed for ${new Date(data.scheduled_for).toLocaleString()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="radio-host-recurring-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4">
      <header className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarClock className="w-4 h-4 text-violet-300 shrink-0" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Recurring Shows</h3>
          <span className="text-[10px] text-white/45">{rows.length}/12</span>
        </div>
        <button
          data-testid="recurring-toggle-form"
          onClick={() => setShowForm((v) => !v)}
          className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-violet-500/20 hover:bg-violet-500/40 text-violet-100 inline-flex items-center gap-1"
        ><Plus className="w-3 h-3" /> {showForm ? "Cancel" : "New"}</button>
      </header>

      {showForm && (
        <div data-testid="recurring-form" className="bg-white/5 border border-white/10 rounded-lg p-2 mb-2 space-y-2">
          <input
            data-testid="recurring-title-input"
            placeholder="Friday Hip-Hop Hour"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 120))}
            className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-[12px] text-white"
          />
          <div className="grid grid-cols-3 gap-2">
            <select
              data-testid="recurring-weekday-select"
              value={weekday}
              onChange={(e) => setWeekday(parseInt(e.target.value, 10))}
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white"
            >
              {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <input
              data-testid="recurring-hour-input"
              type="number" min="0" max="23"
              value={hour}
              onChange={(e) => setHour(Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white text-center"
            />
            <input
              data-testid="recurring-minute-input"
              type="number" min="0" max="59" step="5"
              value={minute}
              onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)))}
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white text-center"
            />
          </div>
          <div className="text-[10px] text-white/55 italic">
            <ListMusic className="w-3 h-3 inline-block" /> Snapshot of current setlist ({currentSetlist.length} tracks)
          </div>
          <button
            data-testid="recurring-save"
            onClick={save}
            disabled={busy}
            className="w-full bg-violet-500 hover:bg-violet-400 disabled:opacity-50 text-black text-[10px] font-black uppercase tracking-wider px-2 py-1.5 rounded"
          >Save Template</button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-[11px] text-white/40 italic">No recurring shows yet — pin your weekly slots so you don't rebuild the setlist every week.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`recurring-${r.id}`}
              className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2 py-1.5"
            >
              <CalendarClock className="w-3.5 h-3.5 text-violet-300 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-white font-bold truncate">{r.title}</div>
                <div className="text-[10px] text-white/45">
                  {WEEKDAYS[r.weekday]} {String(r.hour).padStart(2, "0")}:{String(r.minute || 0).padStart(2, "0")} · {r.tracks?.length || 0} tracks
                  {r.next_occurrence && <span className="ml-1 text-violet-300">→ next {new Date(r.next_occurrence).toLocaleDateString()}</span>}
                </div>
              </div>
              <button
                data-testid={`recurring-${r.id}-materialize`}
                onClick={() => materialize(r.id)}
                title="Arm next occurrence"
                className="p-1.5 rounded bg-violet-500/30 hover:bg-violet-500/50 text-violet-100"
              ><RotateCw className="w-3.5 h-3.5" /></button>
              <button
                data-testid={`recurring-${r.id}-delete`}
                onClick={() => remove(r.id)}
                className="p-1.5 text-white/40 hover:text-rose-300"
              ><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
